// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014

// eslint-disable-next-line import/no-duplicates
import type { MidiMessage } from './soundBufferHolder';
// eslint-disable-next-line import/no-duplicates, no-duplicate-imports
import type SoundBufferHolder from './soundBufferHolder';
import type MusicalClock from './musicalClock';
import type { AnswerPointers, Hit } from '../uTheory.Interaction/rhythmChecker';
import type { RecorderEvent } from './recorder';

interface TablaMidiMessage extends MidiMessage {
  duration?: number;
  tackVelocity?: number;
  tackDelay?: number;
  soundName?: string;
}

export default class TablaInstrument {
  mc: MusicalClock;

  buffer: SoundBufferHolder;

  instrumentName = 'tabla';

  // Public constructor
  constructor(musicalClock?: MusicalClock) {
    if (typeof musicalClock !== 'undefined') {
      this.mc = musicalClock;
      this.buffer = musicalClock.buffer;
    }
  }

  play(evt: RecorderEvent, nextEvt?: AnswerPointers) {
    // Figure out the playback info:
    const playbackInfo = this.getSoundNameAndDuration(evt, nextEvt);
    const velocity = this.getDynamic(evt);
    // Then play it:
    // play(midiOrKey, startTime, stopTime, velocity, toQueue,
    //  absoluteTiming, instrument)
    this.buffer.play(playbackInfo.soundName, {
      velocity,
      startTime: 0,
      stopTime: playbackInfo.duration,
      absoluteTiming: false,
      instrument: this,
    });

    // Cutoff sound...
    if (typeof playbackInfo.tackDelay !== 'undefined') {
      this.buffer.play('tack2', {
        startTime: playbackInfo.tackDelay - 0.01,
        stopTime: undefined,
        velocity: playbackInfo.tackVelocity,
        absoluteTiming: false,
        instrument: this,
      });
    }
  }

  // getMidiMessages(midiInfo) {
  //   const ps = (midiInfo.voice as Voice).getPerformancePointersWithSilences();
  //   const midiMessages = [];
  //   for (let i = 0; i < ps.length; i++) {
  //     const thisP = ps[i];
  //     const nextP = ps[i + 1];
  //     const duration = this.getSoundNameAndDuration(thisP, nextP, midiInfo.tempo);
  //     const velocity = this.getDynamic(thisP);
  //     midiMessages.push({
  //       midiNumber: `tabla${duration.soundName}`,
  //       velocity,
  //       startBeat: thisP.startPointer.toBeats(),
  //       startPointer: thisP.startPointer.clone(),
  //       stopBeat: thisP.stopPointer.toBeats(),
  //       stopPointer: thisP.stopPointer.clone(),
  //     });
  //   }
  //   return midiMessages;
  // }

  reviseMidiMessagesForPlayback(midiMessages: MidiMessage[], tempo?: number): TablaMidiMessage[] {
    // GCR: To-Do if we get a tack delay back, insert/append a new midi message for that
    // releasing tack.
    return midiMessages.map((midiMessage: MidiMessage, i) => {
      const nextMessage = midiMessages[i + 1];
      const soundNameAndDuration = this.getSoundNameAndDuration(midiMessage, nextMessage, tempo);
      return {
        ...midiMessage,
        ...soundNameAndDuration,
      };
    });
  }

  getSoundNameAndDuration(
    evt: MidiMessage | RecorderEvent,
    nextEvt?: MidiMessage | AnswerPointers,
    tempo?: number,
  ): {
    tackVelocity?: number;
    tackDelay?: number;
    duration: number;
    soundName: string;
    midiNumber: string;
    instrument: TablaInstrument;
  } {
    // console.log('evt', evt, 'nextEvt', nextEvt);

    // No info? Just make it a dun
    if (typeof evt === 'undefined') {
      return { soundName: 'dun', midiNumber: 'dun', duration: undefined, instrument: this };
    }

    let soundName = 'dun';
    let tackVelocity: number | undefined;
    let tackDelay: number | undefined;

    // Calculate duration:
    let durationTime = this.calculateDuration(evt, tempo);
    const longSound = durationTime >= 1;
    const veryShort = durationTime <= 0.3;
    const followedByOthers =
      typeof nextEvt !== 'undefined' &&
      evt.stopPointer &&
      evt.stopPointer.equals(nextEvt.startPointer);

    const onBeat = evt.startPointer.s === 0;

    if (longSound) {
      if (followedByOthers) {
        // Long, followed by others, and on beat? dah
        if (onBeat) soundName = 'dah';
        // Long, followed by others, and not on beat? dun-tick
        // else soundName = 'duntick'; this sounds like an error
        else soundName = 'dun';
      } else {
        // Long & not followed by others? boh
        soundName = 'dah';
      }
    } else if (followedByOthers) {
      if (onBeat) {
        // short & followed by other things? dun
        soundName = 'dun';
      } else {
        soundName = 'dun';
      }
    } else {
      // very short & not followed by other things? tack
      // eslint-disable-next-line no-lonely-if
      if (veryShort) {
        soundName = 'dun';
        // tackDelay = durationTime;
        // velocity = -x*5/3 + 1
        // tackVelocity = Math.log(durationTime) * 0.1;
        tackVelocity = ((-durationTime * 4) / 3 + 1) * 0.0155;
      } else {
        // short & not followed by other things? dun...
        soundName = 'dun';
        // tackDelay = durationTime;
        // velocity = -x*5/3 + 1
        // tackVelocity = Math.log(durationTime) * 0.1;
        // tackVelocity = ((-durationTime * 5) / 3 + 1) * 0.0155;
      }
    }

    // Unless it's followed by a rest, let it ring...
    if (followedByOthers) durationTime = undefined;

    if (!followedByOthers && typeof this.mc !== 'undefined') this.panic();

    if (soundName === 'dun') {
      //        soundName = "" + soundName + this.dunCount;
      //        this.dunCount = ++this.dunCount % 3;
      const dunCount = ~~(3 * Math.random());
      soundName = `${soundName}${dunCount}`;
    }

    return {
      soundName,
      tackVelocity,
      tackDelay,
      midiNumber: soundName,
      duration: durationTime,
      instrument: this,
    };
  }

  calculateDuration(evt: MidiMessage | Hit | RecorderEvent, tempo?): number {
    if (!evt.hasOwnProperty('stopPointer')) return 0.99;
    const durationPointer = (evt as MidiMessage | RecorderEvent).stopPointer
      .clone()
      .subtract((evt as MidiMessage | RecorderEvent).startPointer);
    if (typeof tempo !== 'undefined') {
      return durationPointer.toSeconds(tempo);
    }
    return this.mc.getSeconds(durationPointer);
  }

  getDynamic(evt: MidiMessage | RecorderEvent) {
    return evt.velocity ?? 1;
  }

  //   getDynamic(evt: MidiMessage | RecorderEvent) {
  //     if (typeof evt === 'undefined') return 1;
  //     const pointer = evt.startPointer;

  //     // Sets a dynamic for the note based on where it is in the measure
  //     // Begins @ 100%, deducts as fewer criteria are met.

  //     const bpm = pointer.meter.beatsPerMeasure;
  //     const halfBar = bpm % 2 === 0 ? bpm / 2 : false;

  //     let velocity = 1;

  //     switch (pointer.b) {
  //       case 0:
  //         // Downbeat: 100%
  //         break;
  //       case halfBar:
  //         // Half bar: 90%
  //         velocity -= 0.15;
  //         break;
  //       default:
  //         // Other beat: for each beat away from downbeat, subtract 10%
  //         velocity -= (bpm - pointer.b) * 0.15;
  //         break;
  //     }

  //     if (pointer.s !== 0) {
  //       // Other subdivision: for each subdiv away from downbeat, -10%
  //       const spb = pointer.meter.subdivisionsPerBeat;
  //       velocity -= (spb - ~~pointer.s) * 0.25;
  //     }

  //     /*
  //     // Division of a subdivision? subtract an additional 10%
  //     if(pointer.s % 1 !== 0) {
  //       velocity -= 0.15;
  //     }
  // */
  //     return velocity;
  //   }

  panic() {
    const soundnames = ['boh', 'dah', 'dun', 'duntick', 'tack', 'dun0', 'dun1', 'dun2'];
    soundnames.forEach((sound) => {
      this.buffer.noteOff(`tabla${sound}`);
    });
  }
}
